import type { CommentShape } from './types.ts';
import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { createElement, Fragment, useEffect, useState } from 'react';
import {
  PiArrowBendDownRight,
  PiChatCircleSlash,
  PiMinusCircle,
  PiPlusCircle
} from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { emptyCommonI18n, quantityCommonI18n } from '@_/i18n-common';
import { Button, EmptyState, ErrorMessage, LoadingDots } from '@_/ui';
import { useCommentThread } from './useCommentThread.ts';

interface CommentThreadProps {
  depth?: number;
  parentId?: string | null;
  pinned?: boolean;
}

export function CommentThread({ depth = 1, parentId = null }: CommentThreadProps) {
  const [expanded, setExpanded] = useState(depth === 1);

  const { addComments, commentComponent, emptyMessage, fetchQueryArgs, nodeMap, pinnedCommentId } =
    useCommentThread<CommentShape>();

  const query = useInfiniteQuery({
    ...fetchQueryArgs({ parentCommentId: parentId }),
    enabled: expanded,
    getNextPageParam: lastPage => lastPage.pagination.nextCursor,
    initialPageParam: null
  });

  // Add comments to node map when new page of comments is fetched
  useEffect(() => {
    if (query.data?.pages.length) {
      addComments(query.data.pages[query.data.pages.length - 1].data);
    }
  }, [addComments, query.data]);

  if (query.isLoading) {
    return <LoadingDots />;
  }

  const parentNode = nodeMap.get(parentId);
  const parentComment = parentNode?.data;

  const nodes = nodeMap.get(parentId)?.children || [];

  if (!nodes?.length && depth === 1) {
    return (
      <EmptyState
        heading={<FormattedMessage {...emptyCommonI18n.comments} />}
        icon={<PiChatCircleSlash />}
        message={emptyMessage}
      />
    );
  }

  return (
    <div className={clsx(depth > 1 && 'pl-10')}>
      {depth > 1 && (
        <div className="relative">
          {!!(expanded || nodes.find(n => n.isPinned)) && <ThreadLine className="-ml-10" />}

          {!expanded && <ThreadLineBranch className="-ml-10" size="sm" />}

          <div className="py-1 pl-2">
            <Button
              color="primary"
              disabled={query.isFetching}
              iconLeft={expanded ? <PiMinusCircle /> : <PiPlusCircle />}
              onClick={() => setExpanded(!expanded)}
              variant="plain"
            >
              <FormattedMessage
                {...quantityCommonI18n.numReplies}
                values={{
                  num: Math.max(parentComment?.numReplies || 0, parentNode?.children.length || 0)
                }}
              />
            </Button>
          </div>
        </div>
      )}

      {nodes.map((node, index) => (
        <Fragment key={node.id}>
          {(expanded || node.isPinned) && (
            <div className="relative">
              {depth > 1 && expanded && index < nodes.length - 1 && (
                <ThreadLine className="-ml-10" />
              )}

              {depth > 1 && (
                <ThreadLineBranch
                  className="-ml-10"
                  size={node.id === pinnedCommentId ? 'lg' : 'md'}
                />
              )}

              <div className="relative">
                {!!(node.children?.length || node.data?.numReplies) && <ThreadLine root />}

                {node.data &&
                  createElement(commentComponent, {
                    comment: node.data,
                    pinned: node.id === pinnedCommentId
                  })}
              </div>

              {!!(node.children?.length || node.data?.numReplies) && (
                <CommentThread depth={depth + 1} parentId={node.id} />
              )}
            </div>
          )}
        </Fragment>
      ))}

      {expanded && (
        <>
          {query.isError && <ErrorMessage />}

          {query.hasNextPage && (
            <div className="ml-1 py-2 pl-4">
              {query.isFetching ? (
                <LoadingDots />
              ) : (
                <Button
                  color="primary"
                  disabled={query.isFetching}
                  iconLeft={<PiArrowBendDownRight />}
                  onClick={() => query.fetchNextPage()}
                  variant="plain"
                >
                  <FormattedMessage
                    id="component:button.view-more-replies"
                    defaultMessage="View more replies"
                  />
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ThreadLine({ className, root }: { className?: string; root?: boolean }) {
  return (
    <div
      className={clsx(
        'pointer-events-none absolute bottom-0 left-5 w-px bg-gray-100',
        root ? 'top-3' : 'top-0',
        className
      )}
    />
  );
}

function ThreadLineBranch({ className, size }: { className?: string; size: 'sm' | 'md' | 'lg' }) {
  return (
    <div
      className={clsx(
        'pointer-events-none absolute top-0 left-5 w-5 cursor-pointer rounded-bl-[12px] border-b-[1px] border-l-[1px] border-gray-100',
        size === 'sm' && 'h-4',
        size === 'md' && 'h-8',
        size === 'lg' && 'h-14',
        className
      )}
    />
  );
}
